import { ParamMap } from '@angular/router';
import {
  REPAIR_MAINTENANCE_MENU,
  CompanyPageSubMenu,
  CompanyPermission,
  RmNavGroup,
} from '../models/company-permission.model';
import { RoleConstant } from '../models/role-constant';
import { ServiceRequestStatus } from '../models/service-request-status';
import { VendorPendingAction } from '../models/vendor-pending-action';
import {
  getCompanyPermissionForCompany,
  getPageUrlQueryParams,
  normalizeRoutePath,
  parsePageUrlParts,
} from './company-permission.util';

/** Query keys from BE pagePermissions (`repairs-and-maintenance/list?{key}=`). */
export const RM_LIST_TAB_QUERY_KEYS = [
  'overview',
  'pendingapproval',
  'inprogress',
  'draft',
  'allrequests',
  'approvalprocess',
  /** Standalone R&M (no sync to AP): requests waiting to be paid in R&M. */
  'pendingpayment',
  /** Vendor tabs from BE pagePermissions */
  'allworkorders',
  'paymentpending',
] as const;

export type RmListTabQueryKey = (typeof RM_LIST_TAB_QUERY_KEYS)[number];

/** Figma horizontal tab order (Repair & Maintenance revamp). */
export const FIGMA_RM_LIST_TAB_ORDER: readonly RmListTabQueryKey[] = [
  'overview',
  'pendingapproval',
  'inprogress',
  'pendingpayment',
  'draft',
  'allrequests',
  'approvalprocess',
];

/** Figma vendor tab order (Overview → In progress → Payment pending → All Work orders). */
export const VENDOR_RM_LIST_TAB_ORDER: readonly RmListTabQueryKey[] = [
  'overview',
  'inprogress',
  'paymentpending',
  'allworkorders',
];

const VENDOR_RM_LIST_TAB_KEY_SET = new Set<RmListTabQueryKey>(
  VENDOR_RM_LIST_TAB_ORDER
);

export function isVendorRmListTabSet(
  queryKeys: Array<RmListTabQueryKey | null | undefined>
): boolean {
  const keys = queryKeys.filter(
    (key): key is RmListTabQueryKey => key != null
  );
  if (!keys.length) {
    return false;
  }
  return keys.every((key) => VENDOR_RM_LIST_TAB_KEY_SET.has(key));
}

export function resolveRmTabSortOrder(
  queryKeys: Array<RmListTabQueryKey | null | undefined>,
  selectedRoleId?: string | null
): readonly RmListTabQueryKey[] {
  if (isRmVendorRole(selectedRoleId) || isVendorRmListTabSet(queryKeys)) {
    return VENDOR_RM_LIST_TAB_ORDER;
  }
  return FIGMA_RM_LIST_TAB_ORDER;
}

export const RM_LIST_TAB_DISPLAY_LABELS: Partial<
  Record<RmListTabQueryKey, string>
> = {
  overview: 'Overview',
  pendingapproval: 'Pending approval',
  inprogress: 'In progress',
  draft: 'Drafts',
  allrequests: 'All Requests',
  approvalprocess: 'Approval process',
  allworkorders: 'All Work orders',
  paymentpending: 'Payment pending',
  pendingpayment: 'Payment pending',
};

export interface RmListTabViewModel {
  page: string;
  pageUrl: string;
  queryParams: Record<string, string>;
  queryKey: RmListTabQueryKey | null;
}

export const RM_OVERVIEW_STATUS_FILTER_KEY = 'OverviewStatus';
/** Literal OverviewStatus token for the "Created by you" section (not a user id). */
export const RM_OVERVIEW_CREATED_BY_STATUS = 'createdBy';
export const RM_STATUS_FILTER_KEY = 'status';
export const RM_VENDOR_MY_PENDING_ACTIONS_KEY = 'vendorMyPendingActions';

/** Overview tab API page size (each section fetch). */
export const RM_OVERVIEW_TAB_PAGE_SIZE = 24;
/** Max rows rendered per Overview section before See All. */
export const RM_OVERVIEW_SECTION_DISPLAY_SIZE = 4;
export const RM_LIST_DEFAULT_PAGE_SIZE = 10;

export type RmVendorOverviewSectionId =
  | 'quotationToSubmit'
  | 'workToStart'
  | 'invoicesToSubmit';

export type RmOverviewSectionId =
  | 'requestApproval'
  | 'invoiceApproval'
  | 'quotationApproval'
  | 'vendorInvites'
  | 'createdByYou'
  | 'returned'
  | RmVendorOverviewSectionId;

export type RmOverviewSectionDef = {
  id: RmOverviewSectionId;
  label: string;
  icon: string;
  seeAllTab: RmListTabQueryKey;
  /** Client-side section bucket for OverviewStatus `createdBy` rows. */
  useCreatedByFilter?: boolean;
  /** See All → target tab with Request Status preset (e.g. Returned on All Requests). */
  seeAllStatus?: ServiceRequestStatus;
  filters?: Record<string, unknown>;
};

export type RmListTabPreset = {
  mode: 'overview' | 'table' | 'approvalProcess';
  filters?: Record<string, unknown>;
  pageSize?: number;
};

export function rmOverviewStatusFilter(
  statuses: Array<ServiceRequestStatus | string>
): Record<string, Array<ServiceRequestStatus | string>> {
  return { [RM_OVERVIEW_STATUS_FILTER_KEY]: statuses };
}

/** Vendor tab drill-down — BE `ApplyVendorPendingActionFilters` reads this list. */
export function rmVendorPendingActionsFilter(
  actions: VendorPendingAction[]
): Record<string, VendorPendingAction[]> {
  return { [RM_VENDOR_MY_PENDING_ACTIONS_KEY]: actions };
}

/** Vendor Overview grouped sections — status filters for `/search/my`. */
export const RM_VENDOR_OVERVIEW_SECTION_DEFS: readonly RmOverviewSectionDef[] = [
  {
    id: 'quotationToSubmit',
    label: 'Needs quotation',
    icon: '',
    seeAllTab: 'allworkorders',
    filters: rmStatusFilter(ServiceRequestStatus.quotationRequested),
  },
  {
    id: 'workToStart',
    label: 'Work to Start',
    icon: '',
    seeAllTab: 'inprogress',
    filters: rmStatusFilter(ServiceRequestStatus.vendorAssigned),
  },
  {
    id: 'invoicesToSubmit',
    label: 'Invoices to submit',
    icon: '',
    seeAllTab: 'paymentpending',
    filters: rmStatusFilter(ServiceRequestStatus.issueResolved),
  },
];

/** Vendor overview — single fetch covering all section pending-actions. */
export function resolveRmVendorOverviewTabFilters(): Record<string, unknown> {
  return rmVendorPendingActionsFilter([
    VendorPendingAction.quotationRequests,
    VendorPendingAction.assignedWork,
    VendorPendingAction.completedRepairs,
  ]);
}

export const RM_OVERVIEW_SECTION_DEFS: readonly RmOverviewSectionDef[] = [
  {
    id: 'createdByYou',
    label: 'Created by you',
    icon: 'ri-pencil-line',
    seeAllTab: 'allrequests',
    useCreatedByFilter: true,
  },
  {
    id: 'requestApproval',
    label: 'Needs request approval',
    icon: '',
    seeAllTab: 'pendingapproval',
    filters: rmOverviewStatusFilter([ServiceRequestStatus.pendingApproval]),
  },
  {
    id: 'vendorInvites',
    label: 'Needs vendor invites',
    icon: '',
    seeAllTab: 'inprogress',
    filters: rmOverviewStatusFilter([ServiceRequestStatus.approved]),
  },
  {
    id: 'quotationApproval',
    label: 'Needs quotation approval',
    icon: '',
    seeAllTab: 'inprogress',
    filters: rmOverviewStatusFilter([ServiceRequestStatus.quotationRequested]),
  },
  {
    id: 'invoiceApproval',
    label: 'Needs invoice approval',
    icon: '',
    seeAllTab: 'inprogress',
    filters: rmOverviewStatusFilter([
      ServiceRequestStatus.awaitingInvoiceApproval,
    ]),
  },
  {
    id: 'returned',
    label: 'Returned Requests',
    icon: 'ri-arrow-go-back-line',
    seeAllTab: 'allrequests',
    seeAllStatus: ServiceRequestStatus.returned,
    filters: rmOverviewStatusFilter([ServiceRequestStatus.returned]),
  },
];

export const RM_OVERVIEW_APPROVAL_SECTION_IDS: readonly RmOverviewSectionId[] = [
  'requestApproval',
  'invoiceApproval',
  'quotationApproval',
  'vendorInvites',
];

export function isRmOverviewApprovalSection(
  section: RmOverviewSectionId | null | undefined
): boolean {
  return (
    section != null && RM_OVERVIEW_APPROVAL_SECTION_IDS.includes(section)
  );
}

/** OverviewStatus values for the main overview fetch (excludes `createdBy`). */
export function resolveRmOverviewTabStatuses(): Array<ServiceRequestStatus | string> {
  const statuses = RM_OVERVIEW_SECTION_DEFS.filter(
    (section) =>
      !section.useCreatedByFilter &&
      section.filters?.[RM_OVERVIEW_STATUS_FILTER_KEY]
  ).flatMap(
    (section) =>
      section.filters![RM_OVERVIEW_STATUS_FILTER_KEY] as ServiceRequestStatus[]
  );
  return [...new Set(statuses)];
}

/** Main overview API payload — approval/action sections only. */
export function resolveRmOverviewTabFilters(): Record<string, unknown> {
  return rmOverviewStatusFilter(resolveRmOverviewTabStatuses());
}

/** Separate overview fetch for the "Created by you" section. */
export function resolveRmOverviewCreatedByTabFilters(): Record<string, unknown> {
  return rmOverviewStatusFilter([RM_OVERVIEW_CREATED_BY_STATUS]);
}

export type RmOverviewCreatedBySectionData<T> = {
  items: T[];
  total: number;
};

export function partitionRmOverviewSections<
  T extends { status?: string; createdBy?: { id?: string } },
>(
  items: T[],
  userId: string | null | undefined,
  createdBySection?: RmOverviewCreatedBySectionData<T>
): Array<{
  def: RmOverviewSectionDef;
  items: T[];
  total: number;
}> {
  return RM_OVERVIEW_SECTION_DEFS.map((def) => {
    let sectionItems: T[] = [];
    let total = 0;
    if (def.useCreatedByFilter) {
      if (createdBySection) {
        sectionItems = createdBySection.items;
        total = createdBySection.total;
      } else {
        sectionItems = userId
          ? items.filter((item) => item.createdBy?.id === userId)
          : [];
        total = sectionItems.length;
      }
    } else {
      const statuses =
        (def.filters?.[RM_OVERVIEW_STATUS_FILTER_KEY] as
          | ServiceRequestStatus[]
          | undefined) ?? [];
      sectionItems = items.filter((item) =>
        statuses.includes(item.status as ServiceRequestStatus)
      );
      total = sectionItems.length;
    }
    return { def, items: sectionItems, total };
  });
}

/** Vendor overview — bucket rows by `status` for each section def. */
export function partitionRmVendorOverviewSections<
  T extends { status?: string },
>(items: T[]): Array<{
  def: RmOverviewSectionDef;
  items: T[];
  total: number;
}> {
  return RM_VENDOR_OVERVIEW_SECTION_DEFS.map((def) => {
    const expectedStatus = def.filters?.[RM_STATUS_FILTER_KEY];
    const sectionItems =
      typeof expectedStatus === 'string'
        ? items.filter((item) => item.status === expectedStatus)
        : [];
    return { def, items: sectionItems, total: sectionItems.length };
  }).filter((section) => section.total > 0);
}

export function rmStatusFilter(
  status: ServiceRequestStatus
): Record<string, string> {
  return { [RM_STATUS_FILTER_KEY]: status };
}

/** GM / Supervisor / operational roles — creator overview sections. */
export function isRmOverviewOperationalRole(
  roleId: string | null | undefined
): boolean {
  if (!roleId) {
    return false;
  }
  return (
    roleId === RoleConstant.generalManager ||
    roleId === RoleConstant.supervisor ||
    roleId === RoleConstant.directorOperations ||
    roleId === RoleConstant.srDirectorOperations ||
    roleId === RoleConstant.vpOperations
  );
}

export function isRmVendorRole(roleId: string | null | undefined): boolean {
  return roleId === RoleConstant.vendorId;
}

export function getRmOverviewSectionDefs(
  roleId: string | null | undefined
): readonly RmOverviewSectionDef[] {
  return isRmVendorRole(roleId)
    ? RM_VENDOR_OVERVIEW_SECTION_DEFS
    : RM_OVERVIEW_SECTION_DEFS;
}

/** Vendor `/search/my` payload fields (legacy RM list parity). */
export function applyVendorRepairSearchPayload(
  payload: Record<string, unknown>,
  filters?: Record<string, unknown> | null
): void {
  const mergedFilters = {
    ...(payload['repairAndMaintenaceFilters'] as Record<string, unknown> | undefined),
    ...(filters ?? {}),
  };

  payload['status'] = [];
  payload['createdBy'] = [];
  payload['isApprover'] = false;
  payload['isSkipped'] = false;

  const drillPendingActions = mergedFilters[RM_VENDOR_MY_PENDING_ACTIONS_KEY];
  payload['vendorMyPendingActions'] =
    Array.isArray(drillPendingActions) && drillPendingActions.length > 0
      ? drillPendingActions
      : ['None'];
  payload['managerMyPendingActions'] = [];
  payload['assetIds'] = [];

  if (Object.keys(mergedFilters).length > 0) {
    payload['repairAndMaintenaceFilters'] = mergedFilters;
  }
}

/** Roles that approve on Pending approval (bulk + row actions). */
export function isRmApprovalActorRole(
  roleId: string | null | undefined
): boolean {
  if (!roleId) {
    return false;
  }
  return (
    roleId === RoleConstant.companyAdminId ||
    roleId === RoleConstant.RMmanager ||
    isRmOverviewOperationalRole(roleId)
  );
}

export function isRmListTabPageUrl(pageUrl: string): boolean {
  const { path, queryParams } = parsePageUrlParts(pageUrl);
  if (path.toLowerCase() !== 'repairs-and-maintenance/list') {
    return false;
  }
  if (!queryParams) {
    return false;
  }
  return RM_LIST_TAB_QUERY_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(queryParams, key)
  );
}

export function isRmSubMenuRoute(pageUrl: string): boolean {
  const normalized = pageUrl?.trim() ?? '';
  return normalized.length > 0;
}

export function isRmSubMenuRouteActive(
  pageUrl: string,
  currentPathname: string,
  currentQuery?: URLSearchParams | ParamMap | null
): boolean {
  const { path: pagePath, queryParams } = parsePageUrlParts(pageUrl);
  const normalizedPageUrl = pagePath.toLowerCase();
  const normalizedCurrentPath = normalizeRoutePath(currentPathname);

  const pathMatches =
    normalizedPageUrl === 'repairs-and-maintenance/list'
      ? normalizedCurrentPath.startsWith('repairs-and-maintenance')
      : normalizedCurrentPath === normalizedPageUrl ||
        normalizedCurrentPath.startsWith(`${normalizedPageUrl}/`);

  if (!pathMatches) {
    return false;
  }

  if (!queryParams) {
    return true;
  }

  if (
    normalizedPageUrl === 'repairs-and-maintenance/list' &&
    normalizedCurrentPath !== 'repairs-and-maintenance/list'
  ) {
    return false;
  }

  if (!currentQuery) {
    return false;
  }

  const hasKey = (key: string) =>
    currentQuery instanceof URLSearchParams
      ? currentQuery.has(key)
      : currentQuery.has(key);

  return Object.keys(queryParams).every((key) => hasKey(key));
}

export function getRmListTabSubMenus(
  companyPermission: CompanyPermission | null | undefined
): CompanyPageSubMenu[] {
  const pagePermission = companyPermission?.pagePermissions?.find(
    (entry) => entry.menu === REPAIR_MAINTENANCE_MENU
  );

  return (pagePermission?.subMenus ?? []).filter((subMenu) =>
    isRmListTabPageUrl(subMenu.pageUrl)
  );
}

export function getRmListTabs(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): RmListTabViewModel[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  return getRmListTabSubMenus(companyPermission).map((subMenu) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      RM_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;

    return {
      page: subMenu.page,
      pageUrl: subMenu.pageUrl,
      queryParams,
      queryKey,
    };
  });
}

export function sortRmNavSubMenusForDesign(
  subMenus: CompanyPageSubMenu[],
  selectedRoleId?: string | null
): CompanyPageSubMenu[] {
  const tabOrder = resolveRmTabSortOrder(
    (subMenus ?? []).map((subMenu) => {
      const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
      return (
        RM_LIST_TAB_QUERY_KEYS.find((key) =>
          Object.prototype.hasOwnProperty.call(queryParams, key)
        ) ?? null
      );
    }),
    selectedRoleId
  );

  const decorated = (subMenus ?? []).map((subMenu, index) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      RM_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;
    const order = queryKey ? tabOrder.indexOf(queryKey) : -1;
    const page =
      queryKey && RM_LIST_TAB_DISPLAY_LABELS[queryKey]
        ? RM_LIST_TAB_DISPLAY_LABELS[queryKey]!
        : subMenu.page;
    return { subMenu: { ...subMenu, page }, order, index };
  });

  return decorated
    .sort((a, b) => {
      const ai = a.order === -1 ? 1000 + a.index : a.order;
      const bi = b.order === -1 ? 1000 + b.index : b.order;
      return ai - bi;
    })
    .map((entry) => entry.subMenu);
}

export function getRepairNavGroup(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  selectedRoleId?: string | null
): RmNavGroup | null {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  const subMenus = getRmListTabSubMenus(companyPermission);
  if (!subMenus.length) {
    return null;
  }

  const pagePermission = companyPermission?.pagePermissions?.find(
    (entry) => entry.menu === REPAIR_MAINTENANCE_MENU
  );

  return {
    menu: pagePermission?.menu ?? REPAIR_MAINTENANCE_MENU,
    subMenus: sortRmNavSubMenusForDesign(subMenus, selectedRoleId),
  };
}

export function hasRepairSidebar(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  return Boolean(
    getRepairNavGroup(storedCompanyPermissions, companyId)?.subMenus.length
  );
}

export function sortRmListTabsForDesign(
  tabs: RmListTabViewModel[],
  selectedRoleId?: string | null
): RmListTabViewModel[] {
  const tabOrder = resolveRmTabSortOrder(
    tabs.map((tab) => tab.queryKey),
    selectedRoleId
  );

  return [...tabs]
    .sort((a, b) => {
      const ai = a.queryKey ? tabOrder.indexOf(a.queryKey) : -1;
      const bi = b.queryKey ? tabOrder.indexOf(b.queryKey) : -1;
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map((tab) => {
      const label =
        tab.queryKey && RM_LIST_TAB_DISPLAY_LABELS[tab.queryKey]
          ? RM_LIST_TAB_DISPLAY_LABELS[tab.queryKey]!
          : tab.page;
      return { ...tab, page: label };
    });
}

export function resolveRmListTabQueryKey(
  query: ParamMap | URLSearchParams | null | undefined
): RmListTabQueryKey | null {
  if (!query) {
    return null;
  }

  const hasKey = (key: string) =>
    query instanceof URLSearchParams ? query.has(key) : query.has(key);

  return RM_LIST_TAB_QUERY_KEYS.find((key) => hasKey(key)) ?? null;
}

export function resolveRmListTabPreset(
  query: ParamMap | URLSearchParams | null | undefined,
  selectedRoleId?: string | null
): RmListTabPreset {
  const tabKey = resolveRmListTabQueryKey(query);

  switch (tabKey) {
    case 'overview':
      if (isRmVendorRole(selectedRoleId)) {
        return {
          mode: 'overview',
          filters: resolveRmVendorOverviewTabFilters(),
          pageSize: RM_OVERVIEW_TAB_PAGE_SIZE,
        };
      }
      return {
        mode: 'overview',
        filters: resolveRmOverviewTabFilters(),
        pageSize: RM_OVERVIEW_TAB_PAGE_SIZE,
      };
    case 'pendingapproval':
      return {
        mode: 'table',
        filters: rmStatusFilter(ServiceRequestStatus.pendingApproval),
      };
    case 'inprogress':
      if (isRmVendorRole(selectedRoleId)) {
        return {
          mode: 'table',
          filters: rmVendorPendingActionsFilter([
            VendorPendingAction.inProgress,
          ]),
        };
      }
      return {
        mode: 'table',
        filters: rmStatusFilter(ServiceRequestStatus.inProgress),
      };
    case 'draft':
      return {
        mode: 'table',
        filters: rmStatusFilter(ServiceRequestStatus.draft),
      };
    case 'allrequests':
      return { mode: 'table' };
    case 'allworkorders':
      if (isRmVendorRole(selectedRoleId)) {
        return {
          mode: 'table',
          filters: rmVendorPendingActionsFilter([VendorPendingAction.none]),
        };
      }
      return { mode: 'table' };
    case 'paymentpending':
      if (isRmVendorRole(selectedRoleId)) {
        return {
          mode: 'table',
          filters: rmVendorPendingActionsFilter([
            VendorPendingAction.pendingPayments,
          ]),
        };
      }
      return {
        mode: 'table',
        filters: rmStatusFilter(ServiceRequestStatus.paymentPending),
      };
    case 'pendingpayment':
      return {
        mode: 'table',
        filters: rmStatusFilter(ServiceRequestStatus.paymentPending),
      };
    case 'approvalprocess':
      return { mode: 'approvalProcess' };
    default:
      return { mode: 'table' };
  }
}

export function isRmListTabActive(
  tab: RmListTabViewModel,
  pathname: string,
  query: URLSearchParams | ParamMap | null | undefined
): boolean {
  const path = normalizeRoutePath(pathname);
  if (path !== 'repairs-and-maintenance/list') {
    return false;
  }

  if (!tab.queryKey || !query) {
    return false;
  }

  return query instanceof URLSearchParams
    ? query.has(tab.queryKey)
    : query.has(tab.queryKey);
}

export function isRmPendingApprovalTabKey(
  key: RmListTabQueryKey | null
): boolean {
  return key === 'pendingapproval';
}

export function isRmDraftsTabKey(key: RmListTabQueryKey | null): boolean {
  return key === 'draft';
}

export function isRmAllRequestsTabKey(key: RmListTabQueryKey | null): boolean {
  return key === 'allrequests';
}

export function isRmAllWorkOrdersTabKey(key: RmListTabQueryKey | null): boolean {
  return key === 'allworkorders';
}

/** Tabs that show Company (not Location) in the toolbar quick filter. */
export function isRmCompanyToolbarTabKey(key: RmListTabQueryKey | null): boolean {
  return isRmAllRequestsTabKey(key) || isRmAllWorkOrdersTabKey(key);
}

export function isRmApprovalProcessTabKey(
  key: RmListTabQueryKey | null
): boolean {
  return key === 'approvalprocess';
}

/** R&M revamp list — Approval Process tab (when present in pagePermissions). */
export function resolveRmApprovalProcessListNavigation(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): { path: string[]; queryParams: Record<string, string> } | null {
  const approvalTab = getRmListTabs(storedCompanyPermissions, companyId).find(
    (tab) => tab.queryKey === 'approvalprocess'
  );
  if (!approvalTab) {
    return null;
  }

  return {
    path: ['/repairs-and-maintenance/list'],
    queryParams: approvalTab.queryParams,
  };
}
